import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Grid,
  Link,
  Paper,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import { useMutation, useQuery, useQueryClient } from "react-query";

import {
  BeautifulAlerts,
  createAlert,
} from "../../Connectivity/UI/BeautifulAlerts";
import { CustomSelect } from "../../components/Inputs/CustomSelect";
import { CustomTextField } from "../../components/Inputs/CustomTextField";
import { useDebounce } from "../../hooks/debounce";
import CompetitorCard from "./CompetitorCard";
import CompetitorMap from "./CompetitorMap";
import MapLegend from "./MapLegend";
import QualityFilter from "./QualityFilter";
import SelectedList from "./SelectedList";
import SelfProperty from "./SelfProperty";
import {
  CANDIDATES_KEY,
  DEFAULT_INTEREST_RATINGS,
  DEFAULT_RATINGS,
  DEFAULT_STARS,
  INTEREST_OPTIONS,
  PAGE_SIZE,
  RADIUS_CHOICES,
  RATING_OPTIONS,
  SELECTION_KEY,
  STAR_OPTIONS,
  propertyConfigUrl,
} from "./constants";
import {
  addSelection,
  fetchCandidates,
  fetchSelection,
  addSelfSelection,
  stopTracking,
  updateSelection,
} from "./services";
import { Candidate, ContractedProperty, Selection } from "./types";

/**
 * The map half of the selection page: filters, the map, and what is tracked.
 *
 * Split out of `CompetitorSelectionPage` when the historization tab arrived — the
 * property picker stays above the tabs because it scopes both, and everything that
 * only concerns the map came here.
 *
 * ⚠️ Owns its own filter state, so switching to the other tab and back resets it.
 * The alternative was keeping both panels mounted, which cannot work: MapLibre
 * initialises against a zero-size container when its panel is hidden and comes back
 * blank.
 */
const CompetitorMapTab = ({ property }: { property: ContractedProperty }) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const propertyId = property.propertyId;
  const [radiusKm, setRadiusKm] = useState<number | null>(null);
  const [interestRatings, setInterestRatings] = useState<number[]>(
    DEFAULT_INTEREST_RATINGS
  );
  const [stars, setStars] = useState<(number | null)[]>(DEFAULT_STARS);
  const [ratings, setRatings] = useState<(number | null)[]>(DEFAULT_RATINGS);
  const [term, setTerm] = useState("");
  // ⚠️ The typed value drives the input, the debounced one drives the query. A
  // request per keystroke would be the one way this feature *does* cost the server
  // more than it saves: 300 ms turns a ten-character term into one or two calls,
  // each of them cheaper than the unfiltered page it replaces.
  const debouncedTerm = useDebounce(term, 300);
  const [openCandidate, setOpenCandidate] = useState<Candidate | null>(null);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  // ⚠️ The radius default follows the *property*, not the page: it comes from
  // `HebergementLocation.radius_of_interest`, so switching property has to reset it
  // or the client sees another property's radius silently applied to this one.
  useEffect(() => {
    setRadiusKm(property.radiusKm);
    setOpenCandidate(null);
  }, [property]);

  const effectiveRadius = radiusKm ?? property.radiusKm;

  const filters = useMemo(
    () => ({ term: debouncedTerm, interestRatings, stars, ratings }),
    [debouncedTerm, interestRatings, stars, ratings]
  );

  const candidates = useQuery(
    // The filters are part of the key, so changing a box refetches rather than
    // redrawing a stale page. They are applied server-side: the radius can hold
    // thousands of competitors and the page only carries PAGE_SIZE of them, so
    // filtering in the browser would filter whichever slice happened to arrive.
    [CANDIDATES_KEY, propertyId, effectiveRadius, filters],
    () => fetchCandidates(propertyId, effectiveRadius, PAGE_SIZE, 0, filters),
    { enabled: effectiveRadius !== null }
  );

  const selection = useQuery([SELECTION_KEY, propertyId], () =>
    fetchSelection(propertyId)
  );

  const selectedIds = useMemo(
    () => new Set((selection.data ?? []).map((item) => item.competitorCode)),
    [selection.data]
  );

  const add = useMutation(
    (candidate: Candidate) => addSelection(propertyId, candidate.id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([SELECTION_KEY, propertyId]);
        setOpenCandidate(null);
      },
      onError: (error: Error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
    }
  );

  const update = useMutation(
    ({
      item,
      changes,
    }: {
      item: Selection;
      changes: Parameters<typeof updateSelection>[1];
    }) => updateSelection(item.id, changes),
    {
      onMutate: ({ item }) => setBusyId(item.id),
      onSettled: () => setBusyId(null),
      onSuccess: () =>
        queryClient.invalidateQueries([SELECTION_KEY, propertyId]),
      onError: (error: Error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
    }
  );

  /**
   * ⚠️ A separate mutation from `update`, not a third kind of change. It answers 204
   * rather than a row, it cannot be undone from here, and the list has already asked
   * the client to confirm — sharing `update`'s shape would hide all three.
   */
  const remove = useMutation((item: Selection) => stopTracking(item.id), {
    onMutate: (item) => setBusyId(item.id),
    onSettled: () => setBusyId(null),
    onSuccess: () => queryClient.invalidateQueries([SELECTION_KEY, propertyId]),
    onError: (error: Error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  /**
   * ⚠️ The self row is filtered out of the compset list rather than shown in it: it
   * does not count against the allowance and is not something the client picked, so
   * listing it among the rivals would make `n / limit` read wrong.
   */
  const selfSelection =
    (selection.data ?? []).find((item) => item.isSelf) ?? null;
  const competitors = (selection.data ?? []).filter((item) => !item.isSelf);
  const activeCount = competitors.filter((item) => item.isActive).length;
  const limit = property.competitorLimit ?? 0;
  const atLimit = activeCount >= limit;

  const addSelf = useMutation(() => addSelfSelection(propertyId), {
    onSuccess: () => queryClient.invalidateQueries([SELECTION_KEY, propertyId]),
    onError: (error: Error) => {
      // The service refuses with a sentence a client can act on — not yet matched,
      // not enabled — so it is shown rather than swallowed.
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const candidateById = (id: number) =>
    (candidates.data?.items ?? []).find((item) => item.id === id) ?? null;

  return (
    <Stack spacing={2} sx={{ width: "100%", minWidth: 0 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 9 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ minWidth: 200, flexGrow: 1 }}>
              <CustomTextField
                identifier="competitorSearch"
                label={intl.formatMessage({
                  id: "COMPETITOR_SELECTION_SEARCH",
                })}
                value={term}
                required={false}
                size="small"
                onChange={(event) => setTerm(event.target.value)}
              />
            </Box>
            {/* `CustomSelect` is `fullWidth` by design, so the width is bounded here
            rather than by the component. The unit lives in the label because
            `CustomSelect` title-cases each option — "5 km" would render "5 Km". */}
            <Box sx={{ minWidth: 140, maxWidth: 200 }}>
              <CustomSelect
                identifier="radiusKm"
                key={propertyId}
                label={intl.formatMessage({
                  id: "COMPETITOR_SELECTION_RADIUS",
                })}
                defaultValue={String(effectiveRadius ?? "")}
                options={RADIUS_CHOICES.map(String)}
                onChange={(_key, value) =>
                  setRadiusKm(value ? Number(value) : null)
                }
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {/* Its own row: three multi-selects plus those
          two would each be too narrow to show a chip on anything but a wide screen.
          Same grid column as that bar and as the map, so all three left and right
          edges line up at every breakpoint. */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 9 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <QualityFilter
                label={intl.formatMessage({
                  id: "COMPETITOR_SELECTION_INTEREST",
                })}
                tooltip="COMPETITOR_SELECTION_INTEREST_TOOLTIP"
                options={INTEREST_OPTIONS.map((value) => ({
                  value,
                  label: String(value),
                }))}
                selected={interestRatings}
                onChange={setInterestRatings}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <QualityFilter
                label={intl.formatMessage({ id: "COMPETITOR_SELECTION_STARS" })}
                options={[
                  ...STAR_OPTIONS.map((value) => ({
                    value,
                    label: String(value),
                  })),
                  {
                    value: null,
                    label: intl.formatMessage({
                      id: "COMPETITOR_SELECTION_UNRATED",
                    }),
                  },
                ]}
                selected={stars}
                onChange={setStars}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <QualityFilter
                label={intl.formatMessage({
                  id: "COMPETITOR_SELECTION_RATING",
                })}
                tooltip="COMPETITOR_SELECTION_RATING_TOOLTIP"
                options={[
                  ...RATING_OPTIONS.map((value) => ({
                    value,
                    label: String(value),
                  })),
                  {
                    value: null,
                    label: intl.formatMessage({
                      id: "COMPETITOR_SELECTION_UNRATED",
                    }),
                  },
                ]}
                selected={ratings}
                onChange={setRatings}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 9 }}>
          {property && (
            <CompetitorMap
              property={property}
              candidates={candidates.data?.items ?? []}
              // Settled, not successful: a failed candidate fetch must still let the
              // map frame the new property, otherwise it stays on the previous one.
              candidatesSettled={!candidates.isFetching}
              // Numeric, where `selectedIds` is a set of strings: the map keys on
              // `competitor.id` and a string would never match a pin.
              selectedIds={[...selectedIds].map(Number)}
              highlightedId={hoveredId}
              onPointHover={setHoveredId}
              onPointClick={(id) => {
                // Negative ids are the client's own property (see CompetitorMap):
                // it is on the map as the centre, never as something to add.
                if (id < 0) return;
                setOpenCandidate(candidateById(id));
              }}
            />
          )}
          {property && <MapLegend />}
          {/* Two different empty pages, and conflating them was the problem: "nobody
              is near you" is nothing to act on, while "we do not know where you are"
              is one form away from being fixed. The API distinguishes them with
              `error`; so does this. */}
          {/* `!== undefined`, not truthiness: `types.ts` declares that the field's
              *presence* is the signal and its text is not shown, so an empty string is
              an error that a truthy check would route to "no competitor nearby". */}
          {candidates.isSuccess &&
            candidates.data.error !== undefined &&
            property && (
              <Box sx={{ mt: 2 }}>
                <BeautifulAlerts
                  alerts={[
                    createAlert({
                      identifier: `not-geocoded-${property.propertyId}`,
                      type: "warning",
                      closeable: false,
                      message: "COMPETITOR_SELECTION_NOT_GEOCODED",
                      values: {
                        property: property.propertyName,
                        link: (chunks: React.ReactNode) => (
                          <Link
                            href={propertyConfigUrl(property.propertyId)}
                            target="_blank"
                            rel="noopener"
                            underline="always"
                            sx={{
                              color: "warning.contrastText",
                              fontWeight: 700,
                            }}
                          >
                            {chunks}
                          </Link>
                        ),
                      },
                    }),
                  ]}
                  updateAlerts={() => {}}
                />
              </Box>
            )}

          {candidates.isSuccess &&
            candidates.data.error === undefined &&
            candidates.data.total === 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <FormattedMessage id="COMPETITOR_SELECTION_NO_CANDIDATE" />
              </Alert>
            )}
          {candidates.isSuccess && candidates.data.hasMore && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              <FormattedMessage
                id="COMPETITOR_SELECTION_PARTIAL"
                values={{
                  shown: candidates.data.items.length,
                  total: candidates.data.total,
                }}
              />
            </Typography>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <Stack spacing={2}>
            {/* ⚠️ Hidden entirely rather than shown disabled, and both halves of the
                condition matter. `selfConnector` off means the customer never asked
                for this — an explanation of something they cannot buy from here is
                noise. A state other than `READY` means they did ask and Revbell has
                not finished: the self-match is still running, or it failed and there
                is no competitor to track. In every one of those the button could only
                fail, and the fix is not the client's to make. */}
            {property.selfConnector && property.state === "READY" && (
              <SelfProperty
                selection={selfSelection}
                onAdd={() => addSelf.mutate()}
                onDelete={(item) => remove.mutate(item)}
                // ⚠️ The same `busyId` that disables the compset rows, not a flag of
                // its own. The self row deletes through the same route and cascades
                // over the same price and mapping tables, so letting it start while
                // a competitor delete is in flight is exactly the concurrency that
                // deadlocked before.
                busy={busyId !== null || addSelf.isLoading}
                deleting={busyId !== null && busyId === selfSelection?.id}
              />
            )}
            <Paper sx={{ p: 2 }}>
              <SelectedList
                selection={competitors}
                limit={limit}
                busyId={busyId}
                onDelete={(item) => remove.mutate(item)}
                onToggleShown={(item) =>
                  update.mutate({ item, changes: { isShown: !item.isShown } })
                }
              />
            </Paper>
          </Stack>
        </Grid>
      </Grid>

      <Popover
        open={openCandidate !== null}
        anchorReference="anchorPosition"
        anchorPosition={{ top: 200, left: 400 }}
        onClose={() => setOpenCandidate(null)}
      >
        {openCandidate && (
          <CompetitorCard
            candidate={openCandidate}
            adding={add.isLoading}
            // ⚠️ Priceability is deliberately not a gate, and no longer shown at
            // all. Selecting a competitor is what *provisions* its connector —
            // `add_selection` queues `create_booking_connector`, which resolves the
            // property on Booking — so every hotel is priceable and marking some of
            // them otherwise would encode a state that no longer exists. The only
            // refusals left are the two the client created and can undo.
            disabled={selectedIds.has(String(openCandidate.id)) || atLimit}
            disabledReason={
              selectedIds.has(String(openCandidate.id))
                ? intl.formatMessage({ id: "COMPETITOR_SELECTION_TRACKED" })
                : atLimit
                ? intl.formatMessage(
                    { id: "COMPETITOR_SELECTION_LIMIT_REACHED" },
                    { limit }
                  )
                : undefined
            }
            onAdd={(candidate) => add.mutate(candidate)}
          />
        )}
      </Popover>

      {(candidates.isError || selection.isError) && (
        <Alert severity="error">
          <FormattedMessage id="COMPETITOR_SELECTION_LOAD_ERROR" />
        </Alert>
      )}
    </Stack>
  );
};

export default CompetitorMapTab;
